import classNames from 'classnames'
import React, { useEffect, useRef, useState } from 'react'

import {
  capitalizeFirstLetter,
  getTodayDateInputValue
} from '../../../utils/bodyParts/helpers.ts'
import Button from '../Button/Button.tsx'

type FieldType = 'text' | 'number' | 'date' | 'select'

export type SelectOption = {
  label: string
  value: string | number
}

export interface FormField<
  T extends Record<string, unknown> = Record<string, unknown>
> {
  name: string
  label: string
  type?: FieldType
  placeholder?: string
  required?: boolean
  level?: number
  levelLabel?: string
  break?: boolean
  options?: SelectOption[]
  calculatedFrom?: Array<keyof T>
  calculate?: (values: Partial<T>) => string | number
}

interface DataEntryFormProps<T extends Record<string, any>> {
  fields: FormField<T>[]
  onSubmit: (values: T) => void
  submitLabel?: string
  initialValues?: Partial<T>
  editValues?: Partial<T> | null
  mode?: 'add' | 'edit'
  resetAfterSubmit?: boolean
  onClose?: () => void
  showAllLevelsLabel?: string
  hideAllLevelsLabel?: string
}

const DataEntryForm = <T extends Record<string, unknown>>({
  fields,
  onSubmit,
  submitLabel,
  initialValues = {},
  editValues = null,
  mode = editValues ? 'edit' : 'add',
  resetAfterSubmit = mode === 'add',
  onClose,
  showAllLevelsLabel = 'Pokaż wszystkie poziomy',
  hideAllLevelsLabel = 'Ukryj wszystkie poziomy'
}: DataEntryFormProps<T>) => {
  const firstNonDateInputRef = useRef<HTMLInputElement | null>(null)
  const firstNonDateSelectRef = useRef<HTMLSelectElement | null>(null)

  const [manuallyChangedFields, setManuallyChangedFields] = useState<
    Set<string>
  >(() => new Set())

  const [showAllLevels, setShowAllLevels] = useState(false)

  useEffect(() => {
    firstNonDateInputRef.current?.focus()
    firstNonDateSelectRef.current?.focus()
  }, [])

  const getFormValues = (): Partial<T> => {
    if (mode === 'edit' && editValues) {
      return editValues
    }

    const todayDate = getTodayDateInputValue()

    return fields.reduce<Partial<T>>(
      (acc, field) => {
        if (field.type === 'date' && acc[field.name as keyof T] == null) {
          return {
            ...acc,
            [field.name]: todayDate
          }
        }

        if (field.type === 'select' && acc[field.name as keyof T] == null) {
          return {
            ...acc,
            [field.name]: ''
          }
        }

        if (
          field.level === 1 &&
          field.type === 'number' &&
          !field.required &&
          acc[field.name as keyof T] == null
        ) {
          return {
            ...acc,
            [field.name]: 0
          }
        }

        return acc
      },
      { ...initialValues }
    )
  }

  const formValues = getFormValues()

  const [values, setValues] = useState<Partial<T>>(formValues)

  useEffect(() => {
    setValues(formValues)
  }, [])

  useEffect(() => {
    setValues((prev) => {
      let hasChanges = false
      const nextValues = { ...prev }

      fields.forEach((field) => {
        if (!field.calculate) {
          return
        }

        if (manuallyChangedFields.has(field.name)) {
          return
        }

        const calculatedValue = field.calculate(prev)

        if (nextValues[field.name as keyof T] !== calculatedValue) {
          nextValues[field.name as keyof T] = calculatedValue as T[keyof T]
          hasChanges = true
        }
      })

      return hasChanges ? nextValues : prev
    })
  }, [fields, values, manuallyChangedFields])

  const handleChange = (name: string, value: string | number) => {
    const changedField = fields.find((field) => field.name === name)

    if (changedField?.calculate) {
      setManuallyChangedFields((prev) => {
        const next = new Set(prev)
        next.add(name)
        return next
      })
    }

    setValues((prev) => ({
      ...prev,
      [name]: value
    }))
  }

  const getInputValue = (name: string) => {
    return (values[name as keyof T] as string | number | undefined) ?? ''
  }

  const getInputChangeValue = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    type?: string
  ) => {
    if (type === 'number') {
      return event.target.value === '' ? '' : Number(event.target.value)
    }

    if (type === 'text' || type == null) {
      return capitalizeFirstLetter(event.target.value)
    }

    return event.target.value
  }

  const handleFocus = (
    event: React.FocusEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const element = event.currentTarget

    // zaznaczamy tylko w <input>, dla <select> nie ma metody .select()
    if (element instanceof HTMLInputElement) {
      element.select()
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(values as T)

    if (resetAfterSubmit) {
      setValues(getFormValues())
    }
  }

  const firstNonDateFieldIndex = fields.findIndex(
    (field) => field.type !== 'date'
  )

  const fieldsByLevel = fields.reduce<Record<number, FormField<T>[]>>(
    (acc, field) => {
      const level = field.level ?? 0

      if (!acc[level]) {
        acc[level] = []
      }

      acc[level].push(field)

      return acc
    },
    {}
  )

  const fieldsByLevelInArray = Object.values(fieldsByLevel)

  return (
    <form
      onSubmit={handleSubmit}
      className="mb-6 border border-stroke bg-white p-4 shadow-default dark:border-gray-300 dark:bg-boxdark"
    >
      {fieldsByLevelInArray.map((levelFields, levelIndex) => (
        <React.Fragment key={levelIndex}>
          {(showAllLevels ? levelIndex > -1 : levelIndex < 1) && (
            <div
              className={classNames(
                'grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4',
                levelIndex > 0
                  ? 'mt-6 border-t-2 border-gray-200 pt-8 dark:border-gray-600'
                  : ''
              )}
            >
              {levelIndex > 0 && (
                <div className="col-span-full mb-4">
                  <h3 className="text-lg font-semibold text-black dark:text-white">
                    {levelFields[0].levelLabel}
                  </h3>
                </div>
              )}

              {levelFields.map((field, index) => (
                <React.Fragment key={field.name}>
                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-medium text-black dark:text-white">
                      {field.label}
                    </label>

                    {field.type === 'select' ? (
                      <select
                        ref={
                          index === firstNonDateFieldIndex
                            ? firstNonDateSelectRef
                            : undefined
                        }
                        value={getInputValue(field.name)}
                        required={field.required}
                        onChange={(e) =>
                          handleChange(
                            field.name,
                            getInputChangeValue(e, field.type)
                          )
                        }
                        onFocus={handleFocus}
                        className="w-full rounded border border-stroke bg-transparent px-3 py-2 outline-none transition focus:border-blue-500 dark:border-gray-600 dark:text-white dark:focus:border-blue-500"
                      >
                        <option value="" disabled>
                          {field.placeholder ?? 'Wybierz opcję'}
                        </option>

                        {field.options?.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        ref={
                          index === firstNonDateFieldIndex
                            ? firstNonDateInputRef
                            : undefined
                        }
                        type={field.type || 'text'}
                        value={getInputValue(field.name)}
                        placeholder={field.placeholder}
                        required={field.required}
                        onChange={(e) =>
                          handleChange(
                            field.name,
                            getInputChangeValue(e, field.type)
                          )
                        }
                        onFocus={handleFocus}
                        className="w-full rounded border border-stroke bg-transparent px-3 py-2 outline-none transition focus:border-blue-500 dark:border-gray-600 dark:text-white dark:focus:border-blue-500"
                      />
                    )}
                  </div>

                  {field.break && <div className="col-span-full h-0" />}
                </React.Fragment>
              ))}
            </div>
          )}
        </React.Fragment>
      ))}

      <div
        className={
          'mt-4 flex flex-col xl:flex-row justify-between items-center'
        }
      >
        <p className={'text-sm'}>* pola wymagane</p>

        {fieldsByLevelInArray && fieldsByLevelInArray.length > 1 && (
          <div className="mt-4 flex justify-end gap-2">
            <Button
              variant="outlined"
              size="small"
              color="primary"
              onClick={() => setShowAllLevels(!showAllLevels)}
            >
              {showAllLevels ? hideAllLevelsLabel : showAllLevelsLabel}
            </Button>
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <Button
            variant="filled"
            size="small"
            color="warning"
            type="button"
            onClick={onClose}
            className={'flex-1 xl:flex-none'}
          >
            Anuluj
          </Button>
          <Button
            variant="filled"
            size="small"
            color="primary"
            type="submit"
            className={'flex-1 xl:flex-none'}
          >
            {submitLabel ?? (mode === 'edit' ? 'Zapisz zmiany' : 'Dodaj')}
          </Button>
        </div>
      </div>
    </form>
  )
}

export default DataEntryForm
